import React from 'react';

import './AgentProgressTracker.css';
import { useDocProcessing } from './useDocProcessing';

const STATUS_LABELS: Record<string, string> = {
  parsing: 'Reading the document…',
  processing: 'Finding requirements…',
  chunking: 'Organizing sections…',
  embedding: 'Generating embeddings…',
  indexing: 'Indexing content…',
  indexing_complete: 'Indexing complete',
  updating_srd: 'Updating requirements…',
  reconciling: 'Finalizing requirements…',
  complete: 'Complete',
  completed: 'Complete',
  error: 'Processing failed',
};

const SpinnerIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PlanningIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const FileIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" />
    <path d="M9 13h6" />
    <path d="M9 17h4" />
  </svg>
);

const STATUS_STAGE: Record<string, number> = {
  parsing: 0,
  processing: 1,
  chunking: 1,
  embedding: 2,
  indexing: 2,
  indexing_complete: 2,
  updating_srd: 3,
  reconciling: 3,
  complete: 4,
  completed: 4,
};

const STAGES = ['Read', 'Structure', 'Index', 'Update SRD'];

/**
 * Document processing progress shown in the same style as AgentProgressTracker.
 * Blocks the chat input while a document is being indexed.
 */
export const DocProcessingBar: React.FC = () => {
  const state = useDocProcessing();

  if (!state) return null;

  const isDone = state.status === 'completed' || state.status === 'complete';
  const isError = state.status === 'error';
  const label = STATUS_LABELS[state.status] || state.status;
  const pct = Math.min(state.progress, 100);
  const shortName = state.filename.length > 30
    ? `${state.filename.slice(0, 28)}…`
    : state.filename;
  const creditSuffix =
    isDone && state.creditsDeducted != null && state.creditsDeducted > 0
      ? ` · −${state.creditsDeducted.toFixed(2)} cr`
      : '';
  const stageIndex = isError ? -1 : STATUS_STAGE[state.status] ?? 0;

  return (
    <div className={`doc-processing-card${isDone ? ' doc-processing-card--done' : ''}${isError ? ' doc-processing-card--error' : ''}`}>
      <div className="doc-processing-card__header">
        <div className="doc-processing-card__title-group">
          <span className="doc-processing-card__file-icon">
            {isDone || isError ? <FileIcon /> : <PlanningIcon />}
          </span>
          <div>
            <div className="doc-processing-card__title">
              {isError ? 'Document analysis failed' : isDone ? 'Document ready' : 'Analyzing your document'}
            </div>
            <div className="doc-processing-card__filename" title={state.filename}>
              {shortName}
            </div>
          </div>
        </div>
        <span className="doc-processing-card__status">
          {isDone
            ? `Done${creditSuffix}`
            : isError
              ? 'Needs attention'
              : `${pct}%`}
        </span>
      </div>

      <div className="doc-processing-card__body">
        <div className="doc-processing-card__phase">
          <span className="doc-processing-card__phase-icon">
            {isDone ? <CheckIcon /> : isError ? <FileIcon /> : <SpinnerIcon />}
          </span>
          <span>{label}</span>
        </div>

        <div className="doc-processing-card__track">
          <div className="doc-processing-card__fill" style={{ width: isDone ? '100%' : `${pct}%` }} />
        </div>

        <div className="doc-processing-card__stages" aria-label="Document processing stages">
          {STAGES.map((stage, index) => (
            <span
              key={stage}
              className={`doc-processing-card__stage${index < stageIndex || isDone ? ' doc-processing-card__stage--done' : ''}${index === stageIndex && !isDone ? ' doc-processing-card__stage--active' : ''}`}
            >
              {stage}
            </span>
          ))}
        </div>
      </div>

      {!isDone && !isError && <p className="doc-processing-card__hint">Messaging will resume as soon as the requirements update is ready.</p>}
    </div>
  );
};
